/**
 * Display-name helpers — every surface that previously rendered an email
 * now goes through these so we get a consistent first-name-first /
 * email-fallback rule across the app.
 *
 * Per FR-61 the user's first name is the primary handle; we fall back to
 * the email when no first name is set (some accounts pre-date the field
 * and we don't force admins to backfill).
 */

export interface NamedPerson {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

/** Compact handle suitable for headers, sidebars, and table cells. */
export function displayName(p: NamedPerson | undefined): string {
  if (!p) return '';
  const first = (p.firstName ?? '').trim();
  if (first) return first;
  return (p.email ?? '').trim();
}

/** "First Last" if both are set, else first, else email — for cards / detail rows. */
export function fullName(p: NamedPerson | undefined): string {
  if (!p) return '';
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  return (p.email ?? '').trim();
}

/** Initials for avatar circles. Single letter from first name (or email's
 *  first char) when only one is available; two letters when first+last. */
export function initials(p: NamedPerson | undefined): string {
  if (!p) return '';
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  if (first && last) return `${first[0]!}${last[0]!}`.toUpperCase();
  if (first) return first[0]!.toUpperCase();
  const email = (p.email ?? '').trim();
  return email ? email[0]!.toUpperCase() : '';
}
